import { Pipe, PipeTransform } from '@angular/core';

export type DateTimeDisplayType = 'datetime' | 'date' | 'time';

/**
 * Angular pipe for formatting date/datetime strings into localized date, time, or datetime strings
 *
 * Usage in templates:
 * {{ dateTimeValue | formatDateTime: 'datetime' : 'America/New_York' : 'en-US' }}
 * {{ dateTimeValue | formatDateTime: 'date' }}
 * {{ dateTimeValue | formatDateTime: 'time' : 'America/New_York' }}
 * Usage in component:
 * this.formattedDateTime = this.formatDateTimePipe.transform(this.dateTimeValue, 'datetime', 'America/New_York', 'en-US');
 * this.formattedDate = this.formatDateTimePipe.transform(this.dateValue, 'date');
 * this.formattedTime = this.formatDateTimePipe.transform(this.timeValue, 'time', 'America/New_York');
 */
@Pipe({
  name: 'formatDateTime',
})
export class FormatDateTimePipe implements PipeTransform {
  /**
   * Angular pipe transform method - provides the public interface for date/time formatting in templates
   * @param value The date/datetime string to format
   * @param displayType The type of display to use ('datetime', 'date', or 'time'). Defaults to 'datetime'.
   * @param timezone The timezone to use for formatting. E.g 'America/New_York'. Defaults to 'UTC'.
   * @param locale The locale to use for formatting. Defaults to 'en-US'.
   * @returns The formatted date, time, or datetime string.
   */
  transform(value: string | Date | number, displayType: DateTimeDisplayType = 'datetime', timezone?: string, locale: string = 'en-US'): string {
    return this.formatDateTime(value, displayType, timezone, locale);
  }

  /**
   * Formats a date into a localized date, time, or datetime string
   * @param value The date/datetime string to format
   * @param displayType The type of display to use ('datetime', 'date', or 'time'). Defaults to 'datetime'.
   * @param timezone The timezone to use for formatting. E.g 'America/New_York'. Defaults to 'UTC'.
   * @param locale The locale to use for formatting. Defaults to 'en-US'.
   * @returns The formatted date, time, or datetime string.
   */
  private formatDateTime(value: string | Date | number, displayType: DateTimeDisplayType = 'datetime', timezone?: string, locale: string = 'en-US'): string {
    if (!value) {
      return '';
    }

    try {
      const dateObject = new Date(value);

      // Check if date is valid
      if (isNaN(dateObject.getTime())) {
        console.error('Invalid date provided:', value);
        return '';
      }

      const options = this.getFormatOptions(displayType, timezone);

      // format and convert to string
      return dateObject.toLocaleString(locale, options);
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  }

  /**
   * Builds Intl.DateTimeFormatOptions object based on display type and timezone
   * @param displayType The type of display to use ('datetime', 'date', or 'time').
   * @param timezone The timezone to use for formatting. E.g 'America/New_York'. Defaults to 'UTC'.
   * @returns The Intl.DateTimeFormatOptions object.
   */
  private getFormatOptions(displayType: DateTimeDisplayType, timezone?: string): Intl.DateTimeFormatOptions {
    const options: Intl.DateTimeFormatOptions = {};

    // Add date parts for display type 'date' or 'datetime'
    if (displayType === 'date' || displayType === 'datetime') {
      options.year = 'numeric'; // 4-digit year (YYYY)
      options.month = '2-digit'; // MM
      options.day = '2-digit'; // DD
    }

    // Add time parts for display type 'time' or 'datetime'
    if (displayType === 'time' || displayType === 'datetime') {
      options.hour = '2-digit'; // HH
      options.minute = '2-digit'; // mm
    }

    // add timezone if provided
    if (timezone) {
      options.timeZone = timezone; // e.g America/New_York
    }

    return options;
  }
}
